import React from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import { FaTrophy, FaMedal, FaSchool, FaQuoteLeft, FaQuoteRight, FaStar, FaUser } from 'react-icons/fa';
import winnersData from '../data/winnersData';
import OptimizedImage from '../components/utils/OptimizedImage';
import '../styles/winners.css';

const RC2021Page = () => {
  // Animation variants
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };
  
  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.5
      }
    }
  };

  // Filter winners by category - 3 in Junior, 3 in Senior
  const juniorWinners = winnersData.filter(winner => winner.category === "Junior Category");
  const seniorWinners = winnersData.filter(winner => winner.category === "Senior Category");

  // Parent testimonials data from the image
  const testimonials = [
    {
      id: 1,
      name: "Methuka's Father",
      relation: "Junior Category",
      comment: "Not even my son learnt from this competition. As a father I also learnt alot about Arduino and Robotics."
    },
    {
      id: 2,
      name: "Mohomed's Mother",
      relation: "Junior Category",
      comment: "We are Thankful for STEM Labs for organizing such a great competition."
    },
    {
      id: 3,
      name: "Shiwantha's Mother",
      relation: "Senior Category",
      comment: "My son told me that he wants to enroll with their other courses as well. Because they are teaching techniques are very unique and understandable."
    },
    {
      id: 4,
      name: "Pavan's Father",
      relation: "Junior Category",
      comment: "Providing a better service for children with a reasonable price, and making young generation to become an innovative and creative person"
    }
  ];

  // Helper function to determine winner card class based on position
  const getWinnerCardClass = (position) => {
    if (position === '1st Place') return 'winner-card first-place';
    if (position === '2nd Place') return 'winner-card second-place';
    if (position === '3rd Place') return 'winner-card third-place';
    return 'winner-card';
  };

  // Helper function to determine winner position badge class
  const getPositionBadgeClass = (position) => {
    if (position === 'Special Award') return 'winner-position special-award';
    return 'winner-position';
  };

  // Custom image component with fallback
  const WinnerImage = ({ winner }) => {
    return (
      <div className="winner-image">
        {/* Use a div with background to show the winners from the screenshots */}
        <div 
          className="winner-image-fallback"
          style={{ 
            backgroundImage: `url('${winner.image}')`,
            backgroundSize: 'contain',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            width: '100%',
            height: '100%'
          }}
        >
          {/* Fallback icon if image doesn't load */}
          <div className="winner-image-fallback-icon">
            <FaUser size={50} color="#d1d5db" />
          </div>
        </div>
        <div className={getPositionBadgeClass(winner.position)}>
          {winner.position}
        </div>
      </div>
    );
  };

  return (
    <>
      <Helmet>
        <title>RC 2021 Winners | STEM Labs</title>
        <meta name="description" content="Celebrating the outstanding achievements of the Robotics Challenge 2021 competition winners" />
      </Helmet>
      
      {/* Hero Section */}
      <div className="bg-gradient-to-r from-blue-800 to-blue-600 py-16">
        <div className="container mx-auto px-4">
          <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 text-center">
            Robotics Challenge 2021
          </h1>
          <p className="text-white text-lg max-w-3xl mx-auto text-center">
            Celebrating innovation and excellence in robotics among young talent
          </p>
        </div>
      </div>
      
      {/* Junior Category Winners Section */}
      <section className="winner-section">
        <div className="container mx-auto px-4">
          <div className="winner-header">
            <div className="flex justify-center mb-4">
              <FaTrophy className="text-yellow-500 text-6xl" />
            </div>
            <h1 className="text-3xl font-bold text-center mb-8">Junior Category</h1>
            <p className="text-gray-600 max-w-3xl mx-auto text-center mb-12">
              The junior category featured exceptional projects from students in Grade 7-8, showcasing impressive robotics skills and innovative solutions.
            </p>
          </div>
          
          <motion.div 
            className="winners-grid-centered"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {juniorWinners.map((winner) => (
              <motion.div 
                key={winner.id}
                className={getWinnerCardClass(winner.position)}
                variants={itemVariants}
              >
                <WinnerImage winner={winner} />
                <div className="winner-content">
                  <h3 className="winner-name">{winner.name}</h3>
                  <div className="flex items-center mb-3">
                    <FaSchool className="text-gray-400 mr-2" size={14} />
                    <p className="winner-school">{winner.school}</p>
                  </div>
                  <p className="winner-description">{winner.description}</p>
                  <div className="winner-award">
                    <div className="winner-award-name">
                      <FaMedal className="text-yellow-500 inline mr-2" /> {winner.award}
                    </div>
                    <div className="winner-award-year">{winner.category}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      
      {/* Senior Category Winners Section */}
      <section className="winner-section bg-gradient-to-r from-gray-50 to-gray-100">
        <div className="container mx-auto px-4">
          <div className="winner-header">
            <div className="flex justify-center mb-4">
              <FaTrophy className="text-blue-500 text-6xl" />
            </div>
            <h1 className="text-3xl font-bold text-center mb-8">Senior Category</h1>
            <p className="text-gray-600 max-w-3xl mx-auto text-center mb-12">
              Our senior category contestants, in Grade 9, demonstrated remarkable creativity and technical aptitude in developing advanced robotics solutions.
            </p>
          </div>
          
          <motion.div 
            className="winners-grid-centered"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
          >
            {seniorWinners.map((winner) => (
              <motion.div 
                key={winner.id}
                className={getWinnerCardClass(winner.position)}
                variants={itemVariants}
              >
                <WinnerImage winner={winner} />
                <div className="winner-content">
                  <h3 className="winner-name">{winner.name}</h3>
                  <div className="flex items-center mb-3">
                    <FaSchool className="text-gray-400 mr-2" size={14} />
                    <p className="winner-school">{winner.school}</p>
                  </div>
                  <p className="winner-description">{winner.description}</p>
                  <div className="winner-award">
                    <div className="winner-award-name">
                      <FaMedal className="text-blue-500 inline mr-2" /> {winner.award}
                    </div>
                    <div className="winner-award-year">{winner.category}</div>
                  </div>
                </div>
              </motion.div>
            ))}
          </motion.div>
        </div>
      </section>
      
      {/* Parent Testimonials Section */}
      <section className="py-16 bg-gradient-to-r from-blue-50 to-indigo-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">Comments from Parents</h2>
          <p className="text-gray-600 max-w-3xl mx-auto text-center mb-12">
            Hear what parents have to say about their children's experience in the Robotics Challenge 2021
          </p>
          
          <div className="testimonials-container max-w-5xl mx-auto">
            <motion.div 
              className="grid grid-cols-1 md:grid-cols-2 gap-8"
              variants={containerVariants}
              initial="hidden"
              animate="visible"
            >
              {testimonials.map((testimonial) => (
                <motion.div 
                  key={testimonial.id}
                  className="testimonial-card bg-white rounded-2xl shadow-lg overflow-hidden transform transition-transform hover:-translate-y-2 duration-300"
                  variants={itemVariants}
                >
                  <div className="px-6 pt-6 pb-6">
                    <div className="text-center mb-4">
                      <div className="flex justify-center mb-2">
                        {[...Array(5)].map((_, i) => (
                          <FaStar key={i} className="text-yellow-400 mx-0.5" />
                        ))}
                      </div>
                      <h4 className="text-xl font-semibold text-gray-800">{testimonial.name}</h4>
                      <p className="text-sm text-blue-600">{testimonial.relation}</p>
                    </div>
                    <div className="relative pt-3 pb-6">
                      <FaQuoteLeft className="absolute top-0 left-0 text-blue-100 text-xl" />
                      <p className="text-gray-600 text-center px-6 italic">
                        {testimonial.comment}
                      </p>
                      <FaQuoteRight className="absolute bottom-0 right-0 text-blue-100 text-xl" />
                    </div>
                  </div>
                  <div className="h-2 bg-gradient-to-r from-blue-400 to-indigo-500"></div>
                </motion.div>
              ))}
            </motion.div>
          </div>
        </div>
      </section>
      
      {/* Competition Details */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-8 text-center">About the Competition</h2>
          <div className="max-w-4xl mx-auto">
            <div className="bg-white rounded-lg shadow-md p-8">
              <h3 className="text-xl font-bold mb-4 text-blue-700">Robotics Challenge 2021</h3>
              <p className="mb-4">
                The STEM Labs Robotics Challenge 2021 was designed to inspire innovation and foster technical skills among Sri Lankan students. The competition focused on practical problem-solving, creative engineering, and effective implementation of robotics concepts.
              </p>
              <h4 className="text-lg font-semibold mt-6 mb-3 text-gray-700">Competition Categories</h4>
              <ul className="list-disc pl-6 mb-6 text-gray-600 space-y-2">
                <li><strong>Junior Category:</strong> For students in Grades 7-8</li>
                <li><strong>Senior Category:</strong> For students in Grade 9</li>
              </ul>
              <h4 className="text-lg font-semibold mt-6 mb-3 text-gray-700">Judging Criteria</h4>
              <ul className="list-disc pl-6 mb-6 text-gray-600 space-y-2">
                <li><strong>Innovation:</strong> Originality and creativity of the solution</li>
                <li><strong>Technical Design:</strong> Quality of engineering and mechanical implementation</li>
                <li><strong>Programming:</strong> Effectiveness and efficiency of the code</li>
                <li><strong>Functionality:</strong> How well the robot completed assigned tasks</li>
                <li><strong>Presentation:</strong> Communication of ideas and design process</li>
              </ul>
              <div className="bg-blue-50 rounded-lg p-4 mt-6">
                <h4 className="text-lg font-semibold text-blue-700 mb-2">Next Competition</h4>
                <p className="text-gray-700">
                  Stay tuned for information about our upcoming Robotics Challenge 2024! Registration will open soon.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default RC2021Page; 